import Box from '@mui/material/Box';
import useStore, {
  selectCountryTheme,
  selectCountryName,
  selectUserBatch,
  selectDaysForDistribution,
} from '@/hooks/client/useStore';
import { Typography } from '@mui/material';
import PinDropOutlinedIcon from '@mui/icons-material/PinDropOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import FactoryOutlinedIcon from '@mui/icons-material/FactoryOutlined';
import StoreOutlinedIcon from '@mui/icons-material/StoreOutlined';
import { useNavigate } from '@tanstack/react-router';
import Background from '@/common/Background';
import BlurBox from '@/common/BlurBox';
import PrimaryButton from '@/common/PrimaryButton';
import { formatShortDate, addDays } from '@/utils';
import PropTypes from 'prop-types';

const iconMap = {
  location: PinDropOutlinedIcon,
  calendar: CalendarMonthOutlinedIcon,
  factory: FactoryOutlinedIcon,
  store: StoreOutlinedIcon,
};

const IconRenderer = ({ iconName, sx }) => {
  const IconComponent = iconMap[iconName?.toLowerCase()];
  return IconComponent ? <IconComponent sx={sx} /> : null;
};

const css = {
  processDetailsBox: { display: 'flex', alignItems: 'center' },
  processDetailsIcon: (color) => ({
    color: color,
    fontSize: 32,
  }),
  processDetailsContent: {
    padding: 0,
    margin: 0,
  },
  title: {
    color: 'white',
    fontWeight: 'bold',
    py: 2,
    textAlign: 'center',
    fontSize: '1.7rem',
    px: 2,
  },
  batchText: {
    color: 'white',
    fontWeight: 'bold',
    py: 2,
    textAlign: 'center',
    fontSize: '1.2rem',
    px: 2,
  },
  mainBox: { flex: 1, overflow: 'hidden' },
  dashedLine: (color) => ({
    position: 'absolute',
    top: '16px',
    bottom: '16px',
    left: '6px',
    width: '2px',
    borderLeft: `2px dashed ${color}`,
    zIndex: 0,
  }),
  startDot: (color) => ({
    position: 'absolute',
    top: 0,
    left: '1px',
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    bgcolor: color,
    zIndex: 1,
  }),
  stageWrapper: {
    maxHeight: '100%',
    overflowY: 'auto',
    pr: 1,
    pb: 2,
  },
  stagesBox: { marginBottom: '1rem', color: 'white' },
  stageTitle: {
    textTransform: 'uppercase',
    fontSize: '1.1rem',
    fontWeight: '800',
    padding: 0,
    margin: 0,
  },
  endDot: (color) => ({
    position: 'absolute',
    bottom: 0,
    left: '1px',
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    bgcolor: color,
    zIndex: 1,
  }),
  button: {
    marginY: '1rem',
  },
};

const ProcessDetails = ({ color, icon, header = '', title = '', subtitle = '' }) => {
  return (
    <Box data-testid="process-details" sx={css.processDetailsBox}>
      <Box mr={2} minWidth={'30px'}>
        <IconRenderer iconName={icon} sx={css.processDetailsIcon(color)} />
      </Box>
      <Box mb={1} sx={css.processDetailsContent}>
        <Typography>{header}</Typography>
        <Typography fontSize={'0.9rem'}>
          {title}
          <br />
          {subtitle}
        </Typography>
      </Box>
    </Box>
  );
};

const QuickView = ({ data }) => {
  const countryTheme = useStore(selectCountryTheme);
  const userBatch = useStore(selectUserBatch);
  const navigate = useNavigate();
  const countryInfo = useStore(selectCountryName);
  const daysForDistribution = useStore(selectDaysForDistribution);

  if (!Object.keys(data).length) return navigate({ to: '/' });

  const handleViewDetails = () => navigate({ to: '/detailed-view' });

  return (
    <Background bg={'quick-view-bg.png'}>
      <BlurBox>
        <Typography variant="subtitle1" sx={css.title}>
          El recorrido del pollo que compras
        </Typography>
        <Typography sx={css.batchText}>Lote de producto: {userBatch}</Typography>

        <Box position="relative" pl={4} sx={css.mainBox}>
          <Box sx={css.dashedLine(countryTheme.iconColor)} />
          <Box sx={css.startDot(countryTheme.iconColor)} />
          <Box sx={css.stageWrapper}>
            <Box sx={css.stagesBox}>
              {/* Granja */}
              <Box mb={2}>
                <Typography sx={css.stageTitle}>GRANJA</Typography>
                <ProcessDetails
                  color={countryTheme.iconColor}
                  header={`Criados en granjas ${countryInfo.demonym}`}
                  icon={'location'}
                  title={'Región donde se crió:'}
                  subtitle={data.farm_region}
                />
                <ProcessDetails
                  color={countryTheme.iconColor}
                  icon={'calendar'}
                  title={'Fecha de salida del pollo:'}
                  subtitle={formatShortDate(data.chicken_dispatch_date)}
                />
              </Box>
              {/* Planta */}
              <Box mb={2}>
                <Typography sx={css.stageTitle}>PLANTA DE PROCESOS</Typography>
                <ProcessDetails
                  color={countryTheme.iconColor}
                  icon={'factory'}
                  title={'Ubicación de la planta:'}
                  subtitle={data.plant_name}
                />
                <ProcessDetails
                  color={countryTheme.iconColor}
                  icon={'calendar'}
                  title={'Fecha de elaboración:'}
                  subtitle={formatShortDate(data.production_date)}
                />
              </Box>
              {/* Distribucion */}
              <Box mb={2}>
                <Typography sx={css.stageTitle}>DISTRIBUCI&oacute;N</Typography>
                <ProcessDetails
                  color={countryTheme.iconColor}
                  icon={'store'}
                  title={'Fecha de inicio de distribución del lote entre: '}
                  subtitle={`${formatShortDate(data.warehouse_exit_date)} - ${addDays(data.warehouse_exit_date, daysForDistribution)}`}
                />
                <ProcessDetails
                  title={`Tiempo transcurrido entre el proceso y el inicio de la distribución: De 1 a ${daysForDistribution} días`}
                />
              </Box>
            </Box>
          </Box>
          <Box sx={css.endDot(countryTheme.iconColor)} />
        </Box>
      </BlurBox>

      {/* See more button */}
      <PrimaryButton text={'Conoce más'} onClick={handleViewDetails} sx={css.button} />
    </Background>
  );
};

QuickView.propTypes = {
  data: PropTypes.object.isRequired,
};

ProcessDetails.propTypes = {
  color: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  header: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
};

IconRenderer.propTypes = {
  iconName: PropTypes.string.isRequired,
  sx: PropTypes.string.isRequired,
};

export default QuickView;
